import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useCms } from '../context/CmsContext';
import {
  Award,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Quote,
  Building2,
} from 'lucide-react';

interface CertificateData {
  id: string;
  badge: string;
  titlePrefix: string;
  titleHighlight: string;
  overview: string;
  recipient: string;
  recipientRole?: string;
  issuingAuthority: string;
  signatories: string;
  validity: string;
  scopeText: string;
  imageSrc: string;
  imageAlt: string;
}

const CERTIFICATES: CertificateData[] = [
  {
    id: 'qicc-simatech',
    badge: 'OFFICIAL CORPORATE RECOGNITION',
    titlePrefix: 'QICC Nexans',
    titleHighlight: 'Certificate Of Appreciation',
    overview:
      'Simatech Automation India holds official recognition and certificate of appreciation from QICC (A Nexans Company). This honors our sustained engineering support, Siemens automation orchestration, and turnkey drive commissioning across high-capacity industrial cable plants.',
    recipient: 'SIMATECH AUTOMATION INDIA',
    recipientRole: 'Turnkey Industrial Automation & System Integrator',
    issuingAuthority: 'QICC (A Nexans Company) // شركة قطر العالمية للكابلات',
    signatories: 'Xavier Mathieu (GM & CEO) & Nervana Eltanbouly (HR Director)',
    validity: 'Official Corporate Appreciation',
    scopeText: '"For the continued professional support in Automation extended to QICC over the years."',
    imageSrc: '/assets/certificates/qicc_cert_2.webp',
    imageAlt: 'QICC A Nexans Company - Certificate of Appreciation to Simatech Automation India',
  },
  {
    id: 'qicc-sushil',
    badge: 'TECHNICAL SPECIALIST RECOGNITION',
    titlePrefix: 'QICC Nexans',
    titleHighlight: 'Engineering Appreciation',
    overview:
      'Presented to Sushil Kumar Dubey from Simatech Automation India in recognition of relentless technical expertise, multi-axis drive synchronization, and mission-critical PLC automation assistance provided to QICC over the years.',
    recipient: 'SUSHIL KUMAR DUBEY',
    recipientRole: 'Automation Specialist & Simatech Engineering Representative',
    issuingAuthority: 'QICC (A Nexans Company) // شركة قطر العالمية للكابلات',
    signatories: 'Xavier Mathieu (GM & CEO) & Nervana Eltanbouly (HR Director)',
    validity: 'Official Specialist Recognition',
    scopeText: '"From SIMATECH AUTOMATION INDIA for the continued professional support in Automation extended to QICC over the years."',
    imageSrc: '/assets/certificates/qicc_cert_1.webp',
    imageAlt: 'QICC A Nexans Company - Certificate of Appreciation to Sushil Kumar Dubey',
  },
  {
    id: 'qicc-kunal',
    badge: 'FIELD LEADERSHIP RECOGNITION',
    titlePrefix: 'QICC Nexans',
    titleHighlight: 'Professional Support Honor',
    overview:
      'Presented to Kunal Suresh Vajirkar from Simatech Automation India honoring exceptional field engineering execution, control architecture reliability, and dedicated ongoing automation support extended to QICC over the years.',
    recipient: 'KUNAL SURESH VAJIRKAR',
    recipientRole: 'Field Engineering Lead & Automation Engineer',
    issuingAuthority: 'QICC (A Nexans Company) // شركة قطر العالمية للكابلات',
    signatories: 'Xavier Mathieu (GM & CEO) & Nervana Eltanbouly (HR Director)',
    validity: 'Official Engineering Recognition',
    scopeText: '"From SIMATECH AUTOMATION INDIA for the continued professional support in Automation extended to QICC over the years."',
    imageSrc: '/assets/certificates/qicc_cert_3.webp',
    imageAlt: 'QICC A Nexans Company - Certificate of Appreciation to Kunal Suresh Vajirkar',
  },
];

export const CertificationsSection: React.FC = () => {
  const { siteData } = useCms();
  const homeData = siteData?.pages?.home;
  const sectionBgColor =
    homeData?.certsSectionBgColor && homeData.certsSectionBgColor.toLowerCase() !== '#ebedf4'
      ? homeData.certsSectionBgColor
      : '#040f12';

  const certsBadge = homeData?.certificationsBadge || homeData?.certsSectionBadge || 'OFFICIAL ACCREDITATION & CERTIFICATION';
  const certsHeading = homeData?.certificationsHeading || homeData?.certsSectionHeading;
  const certsSubheading = homeData?.certificationsSubheading || homeData?.certsSectionSubheading;

  // Dynamic typography overrides
  const headingFontFamily = homeData?.certsHeadingFontFamily || "'Jost', sans-serif";
  const headingFontSize = homeData?.certsHeadingFontSize;
  const headingFontWeight = homeData?.certsHeadingFontWeight || '300';
  const headingColor = homeData?.certsHeadingColor || '#ffffff';

  const subheadingFontFamily = homeData?.certsSubheadingFontFamily || "'Google Sans Flex', sans-serif";
  const subheadingFontSize = homeData?.certsSubheadingFontSize;
  const subheadingColor = homeData?.certsSubheadingColor || '#cbd5e1';

  const allCertificates = React.useMemo(() => {
    if (Array.isArray(homeData?.certificates) && homeData.certificates.length > 0) {
      return homeData.certificates.map((c: any, idx: number) => {
        const fallback = CERTIFICATES[idx] || CERTIFICATES[0];
        const effectiveImg = c.imageSrc || c.image || fallback.imageSrc || '/assets/certificates/qicc_cert_1.webp';
        return {
          id: c.id || fallback.id || `cert-${idx}`,
          badge: c.badge || fallback.badge || 'OFFICIAL ACCREDITATION',
          titlePrefix: c.titlePrefix || fallback.titlePrefix || 'Official Accreditation',
          titleHighlight: c.titleHighlight || c.title || fallback.titleHighlight || 'Certification of Standards',
          overview:
            c.overview ||
            fallback.overview ||
            `Presented to ${c.recipient || 'Simatech Automation India'} in recognition of technical expertise, quality compliance, and dedicated turnkey engineering services.`,
          recipient: c.recipient || fallback.recipient || 'SIMATECH AUTOMATION INDIA',
          recipientRole: c.recipientRole || fallback.recipientRole || 'Turnkey Industrial Automation & System Integrator',
          issuingAuthority: c.issuingAuthority || c.authority || fallback.issuingAuthority || 'Authorized Accreditations Board',
          signatories: c.signatories || fallback.signatories || 'Authorized Audit Committee',
          validity: c.validity || fallback.validity || 'Active Certification',
          scopeText: c.scopeText || fallback.scopeText || '"For continued engineering excellence, standards compliance, and professional automation support."',
          imageSrc: effectiveImg,
          imageAlt: c.imageAlt || c.title || c.recipient || fallback.imageAlt || 'Official Certificate Scan',
        };
      });
    }
    if (homeData?.certsSectionImageUrl) {
      const customCert: CertificateData = {
        id: 'custom-admin-cert',
        badge: 'ACCREDITATION CERTIFICATE',
        titlePrefix: 'Official Accreditation',
        titleHighlight: 'Certification of Standards',
        overview:
          homeData?.certsSectionSubheading?.replace(/<[^>]+>/g, '') ||
          'Official accreditation verifying compliance with international industrial engineering, automation, and safety benchmarks.',
        recipient: 'SIMATECH AUTOMATION INDIA',
        recipientRole: 'Turnkey Industrial Automation & System Integrator',
        issuingAuthority: 'Accredited Verification Authority',
        signatories: 'Authorized Audit Board',
        validity: 'Active Compliance',
        scopeText: '"Verified and certified for turnkey automation engineering and panel design."',
        imageSrc: homeData.certsSectionImageUrl,
        imageAlt: 'Simatech Automation Official Certification',
      };
      return [customCert, ...CERTIFICATES];
    }
    return CERTIFICATES;
  }, [homeData?.certificates, homeData?.certsSectionImageUrl, homeData?.certsSectionSubheading]);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isZoomOpen, setIsZoomOpen] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const activeCert = allCertificates[currentIndex] || allCertificates[0];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? allCertificates.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === allCertificates.length - 1 ? 0 : prev + 1));
  };

  // Keyboard navigation for carousel & modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isZoomOpen) {
        if (e.key === 'Escape') setIsZoomOpen(false);
        if (e.key === 'ArrowLeft') handlePrev();
        if (e.key === 'ArrowRight') handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZoomOpen, allCertificates.length]);

  return (
    <section
      id="certifications"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-24 text-white relative overflow-hidden border-t border-[#1b7481]/30 scroll-mt-24"
      style={{ backgroundColor: sectionBgColor }}
    >
      {/* Background Ambient Radial Lights & Grid */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1b7481]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-[#38c9de]/10 rounded-full blur-[100px] pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(#38c9de 1px, transparent 1px), linear-gradient(90deg, #38c9de 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Optional Section Header (Configured in Admin Portal) */}
        {(certsHeading || certsSubheading) && (
          <div
            className={`mb-10 sm:mb-12 max-w-3xl transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-xs font-mono uppercase tracking-widest text-[#38c9de] font-semibold mb-2 sm:mb-2.5">
              {certsBadge}
            </div>
            {certsHeading && (
              <h2
                className="text-lg sm:text-[22px] md:text-[27px] lg:text-[32px] font-light tracking-tight leading-[1.25] mb-3"
                style={{
                  fontFamily: headingFontFamily,
                  fontSize: headingFontSize || undefined,
                  fontWeight: headingFontWeight,
                  color: headingColor,
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: certsHeading }} />
              </h2>
            )}
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#1b7481] to-[#38c9de] mb-4" />
            {certsSubheading && (
              <div
                className="font-light text-[14.5px] leading-relaxed [&_strong]:font-semibold [&_a]:text-[#38c9de]"
                style={{
                  fontFamily: subheadingFontFamily,
                  fontSize: subheadingFontSize || undefined,
                  color: subheadingColor,
                }}
                dangerouslySetInnerHTML={{ __html: certsSubheading }}
              />
            )}
          </div>
        )}

        {/* Section Wrapper */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center transition-all duration-700 delay-200 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          
          {/* ========================================================================= */}
          {/* LEFT COLUMN: RE-ENGINEERED ULTRA-CLEAN CERTIFICATE SHOWCASE               */}
          {/* ========================================================================= */}
          <div className="lg:col-span-5 flex flex-col items-center">
            
            {/* Main Interactive Showcase Plaque */}
            <div className="w-full relative group">
              
              {/* Certificate Canvas Frame (Border Removed) */}
              <div
                onClick={() => setIsZoomOpen(true)}
                className={`w-full relative bg-white p-2 sm:p-2.5 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.7)] border-0 transition-all duration-300 hover:shadow-[0_25px_70px_rgba(27,116,129,0.35)] cursor-pointer overflow-hidden`}
                style={activeCert.badge === 'OFFICIAL CORPORATE RECOGNITION' ? { border: '0.5px solid #fff' } : {}}
              >
                {/* Certificate High-Res Image Display */}
                <div className="relative w-full aspect-[16/10.2] bg-white rounded-lg overflow-hidden flex items-center justify-center border-0">
                  <img
                    key={activeCert.id}
                    src={activeCert.imageSrc}
                    alt={activeCert.imageAlt}
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />

                  {/* Sleek Zoom Badge Overlay */}
                  <div className="absolute top-2.5 right-2.5 bg-[#06242a]/85 backdrop-blur-md text-[#38c9de] border border-[#38c9de]/30 px-2.5 py-1 rounded-full text-[10px] font-mono flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition-all shadow-md">
                    <Maximize2 className="w-3 h-3" />
                    <span>ZOOM</span>
                  </div>
                </div>
              </div>

              {/* Seamless Floating Side Navigation Arrows */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                className="absolute -left-3.5 sm:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#06242a]/90 backdrop-blur-md border border-white/20 hover:border-[#38c9de] hover:bg-[#1b7481] text-white flex items-center justify-center transition-all duration-200 cursor-pointer shadow-xl z-20 hover:scale-110 active:scale-95"
                aria-label="Previous Certificate"
              >
                <ChevronLeft className="w-5 h-5 text-[#38c9de] group-hover/btn:text-white" />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="absolute -right-3.5 sm:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#06242a]/90 backdrop-blur-md border border-white/20 hover:border-[#38c9de] hover:bg-[#1b7481] text-white flex items-center justify-center transition-all duration-200 cursor-pointer shadow-xl z-20 hover:scale-110 active:scale-95"
                aria-label="Next Certificate"
              >
                <ChevronRight className="w-5 h-5 text-[#38c9de] group-hover/btn:text-white" />
              </button>

            </div>

            {/* Clean Segmented Indicator Bar & Thumbnails */}
            <div className="w-full mt-5 flex flex-col gap-3">
              
              {/* Pagination Dots & Active Counter */}
              <div className="flex items-center justify-between px-1 text-xs font-mono">
                <span className="text-slate-400">
                  DOCUMENT <span className="text-[#38c9de] font-bold">0{currentIndex + 1}</span> / 0{allCertificates.length}
                </span>

                <div className="flex items-center gap-1.5">
                  {allCertificates.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                        currentIndex === idx
                          ? 'w-6 bg-[#38c9de]'
                          : 'w-2 bg-white/20 hover:bg-white/40'
                      }`}
                      aria-label={`Jump to certificate ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Interactive Thumbnail Cards Row */}
              <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                {allCertificates.map((cert, idx) => {
                  const isSelected = currentIndex === idx;
                  return (
                    <button
                      key={cert.id}
                      type="button"
                      onClick={() => setCurrentIndex(idx)}
                      className={`p-1.5 rounded-lg border text-left transition-all duration-300 cursor-pointer flex flex-col items-center justify-between ${
                        isSelected
                          ? 'bg-[#1b7481]/25 border-[#38c9de] ring-1 ring-[#38c9de]/40 shadow-lg shadow-[#1b7481]/20'
                          : 'bg-white/[0.03] border-white/10 hover:border-white/30 hover:bg-white/[0.06] opacity-70 hover:opacity-100'
                      }`}
                    >
                      <div className="w-full aspect-[16/10] rounded overflow-hidden bg-white mb-1.5 border border-white/10">
                        <img
                          src={cert.imageSrc}
                          alt={cert.recipient}
                          loading="lazy"
                          decoding="async"
                          width={160}
                          height={100}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className={`text-[10px] font-mono truncate w-full text-center ${
                        isSelected ? 'text-[#38c9de] font-medium' : 'text-slate-400'
                      }`}>
                        {String(cert.recipient || cert.titlePrefix || 'Cert').split(' ')[0]}
                      </span>
                    </button>
                  );
                })}
              </div>

            </div>

          </div>

          {/* ========================================================================= */}
          {/* RIGHT COLUMN: STRUCTURED METADATA & ACCREDITATION DETAILS (BRAND COLORS)  */}
          {/* ========================================================================= */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            
            {/* 1. Top Eyebrow Badge with Brand Ribbon / Award Icon */}
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#38c9de] font-semibold mb-3 sm:mb-3.5">
              <Award className="w-4 h-4 text-[#38c9de]" />
              <span>{activeCert.badge}</span>
            </div>

            {/* 2. Large Main Heading with Brand Gradient */}
            <h2 className="text-[22px] sm:text-[27px] lg:text-[32px] font-light text-white tracking-tight leading-[1.25] font-['Jost',sans-serif] mb-3.5 sm:mb-4">
              {activeCert.titlePrefix}{' '}
              <span className="font-normal text-transparent bg-clip-text bg-gradient-to-r from-[#38c9de] via-[#81d8e5] to-[#1b7481]">
                {activeCert.titleHighlight}
              </span>
            </h2>
            <div className="h-[1px] w-16 bg-gradient-to-r from-[#38c9de] to-[#1b7481] mb-4" />

            {/* 3. Description Paragraph */}
            <p className="text-[#a0bec4] text-[15px] sm:text-[16px] leading-relaxed font-light font-['Google_Sans_Flex',sans-serif] mb-6 sm:mb-8 max-w-2xl">
              {activeCert.overview}
            </p>



            {/* 5. Modern Scope of Appreciation & Executive Endorsement Card */}
            <div className="w-full">
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#07252c]/90 via-[#03161b]/85 to-[#020b0e]/95 border border-[#38c9de]/30 p-5 sm:p-6 shadow-2xl backdrop-blur-md transition-all duration-300 hover:border-[#38c9de]/50 group">
                
                {/* Subtle Ambient Radial Glow */}
                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#38c9de]/10 rounded-full blur-2xl pointer-events-none group-hover:bg-[#38c9de]/15 transition-all duration-500" />
                <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-[#1b7481]/15 rounded-full blur-2xl pointer-events-none" />

                {/* Styled Quote Body with Large Quotation Mark */}
                <div className="relative pt-1 pb-3 flex items-start gap-3.5 z-10">
                  <Quote className="w-7 h-7 text-[#38c9de]/40 shrink-0 mt-0.5 rotate-180" />
                  <blockquote className="text-[15px] sm:text-[16px] text-slate-100 font-light leading-relaxed font-['Jost',sans-serif] tracking-wide">
                    “{activeCert.scopeText ? activeCert.scopeText.replace(/^["“]|["”]$/g, '').trim() : ''}”
                  </blockquote>
                </div>

                {/* Bottom Authority & Recipient Metadata Row */}
                <div className="pt-3.5 border-t border-[#1b7481]/20 flex flex-wrap items-center justify-between gap-3 text-xs relative z-10">
                  {activeCert.issuingAuthority && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#1b7481]/25 border border-[#38c9de]/25 flex items-center justify-center text-[#38c9de] shrink-0">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">Issuing Authority</span>
                        <span className="text-xs font-medium text-slate-200">{activeCert.issuingAuthority}</span>
                      </div>
                    </div>
                  )}

                  {activeCert.recipient && (
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#1b7481]/25 border border-[#38c9de]/25 flex items-center justify-center text-[#38c9de] shrink-0">
                        <Award className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">Presented To</span>
                        <span className="text-xs font-medium text-[#38c9de]">{activeCert.recipient}</span>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

          </div>

        </div>

      </div>

      {/* ========================================================================= */}
      {/* FULLSCREEN LIGHTBOX MODAL (CLICK TO ZOOM)                                 */}
      {/* ========================================================================= */}
      {isZoomOpen && (
        <div
          className="fixed inset-0 z-[999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
          onClick={() => setIsZoomOpen(false)}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-2xl p-4 sm:p-6 shadow-2xl border border-white/20 animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200">
              <div>
                <h4 className="text-lg font-bold text-[#06242a] font-['Jost',sans-serif]">
                  {activeCert.recipient}
                </h4>
                <p className="text-xs text-slate-500 font-mono">
                  {activeCert.issuingAuthority}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsZoomOpen(false)}
                className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-[#06242a] flex items-center justify-center transition-colors cursor-pointer"
                aria-label="Close Preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Zoomed Certificate Image */}
            <div className="w-full max-h-[70vh] flex items-center justify-center overflow-auto rounded-xl bg-slate-50 p-2">
              <img
                src={activeCert.imageSrc}
                alt={activeCert.imageAlt}
                loading="lazy"
                decoding="async"
                className="max-w-full max-h-[65vh] object-contain rounded shadow-md"
              />
            </div>

            {/* Modal Footer Controls */}
            <div className="mt-4 flex items-center justify-between text-xs text-slate-600 font-mono">
              <button
                type="button"
                onClick={handlePrev}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-[#06242a] font-medium rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span>{currentIndex + 1} of {allCertificates.length}</span>
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 bg-[#1b7481] hover:bg-[#06242a] text-white font-medium rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
